import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatListModule } from '@angular/material/list';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

import { User, Chatbot } from '../../../core/models/user.model';
import { UserManagementService } from '../../../core/services/user-management.service';
import { ChatbotManagementService } from '../../../core/services/chatbot-management.service';

import { LucideAngularModule, Bot } from "lucide-angular";

export interface UserEditModalData {
  user: User | null;
  allChatbots: Chatbot[];
}

@Component({
  selector: 'app-user-edit-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatListModule,
    MatSnackBarModule,
    LucideAngularModule
  ],
  template: `
    <div class="user-edit-modal bg-light-surface dark:bg-dark-surface flex flex-col max-h-[90vh]">
      <!-- Header -->
      <h1 class="titulo">
        {{ isEditMode ? 'Editar Usuario' : 'Agregar Usuario' }}
      </h1>
      <!-- Content with scroll -->
      <mat-dialog-content class="flex-1 overflow-y-auto !px-0 !pt-1 dark:[color-scheme:dark]">
        @if (userForm) {
          <form [formGroup]="userForm" class="space-y-4">
            <!-- Mostrar ID y Estado Activo en modo edición -->
            @if (isEditMode && data.user) {
              <div class="flex flex-col md:flex-row gap-4">
                <!-- ID de Usuario -->
                <div class="flex-1 px-3 py-2 bg-light-bg-secondary dark:bg-dark-surface-variant rounded-xl border border-light-border dark:border-dark-border flex items-center gap-2">
                  <span class="text-xs text-light-text-tertiary dark:text-dark-text-tertiary whitespace-nowrap">ID de Usuario:</span>
                  <span class="text-sm font-semibold text-light-text dark:text-dark-text">{{ data.user.userId }}</span>
                </div>
                <!-- Estado Activo -->
                <div class="flex-1 flex items-center px-3 py-2 bg-light-bg-secondary dark:bg-dark-surface-variant rounded-xl border border-light-border dark:border-dark-border">
                  <mat-checkbox formControlName="isActive" [disabled]="loading || saving" color="primary">
                    <span class="text-light-text dark:text-dark-text font-medium">Usuario activo</span>
                  </mat-checkbox>
                </div>
              </div>
            }
            <!-- Email -->
            <mat-form-field appearance="fill" class="w-full" >
              <mat-label>Email</mat-label>
              <input matInput formControlName="email" type="email" placeholder="usuario@ejemplo.com"
                [disabled]="saving"
                class="text-light-text dark:text-dark-text">
              @if (userForm.get('email')?.hasError('required')) {
                <mat-error>
                  El email es requerido
                </mat-error>
              }
              @if (userForm.get('email')?.hasError('email')) {
                <mat-error>
                  Ingrese un email válido
                </mat-error>
              }
              @if (userForm.get('email')?.hasError('emailExists')) {
                <mat-error>
                  Este email ya está registrado
                </mat-error>
              }
            </mat-form-field>
            <div class="border-t border-light-border dark:border-dark-border"></div>
            <!-- Chatbots Section -->
            <div class="chatbots-section">
              <h3 class="text-lg font-semibold text-light-text dark:text-dark-text mb-4 flex items-center gap-2">
                <lucide-angular
                  [img]="Bot"
                  class="text-4xl text-light-primary dark:text-dark-primary"
                ></lucide-angular>
                Chatbots Disponibles
              </h3>
              @if (loading) {
                <div class="flex justify-center py-8">
                  <mat-spinner diameter="30"></mat-spinner>
                </div>
              }
              @if (!loading && chatbots.length > 0) {
                <mat-selection-list
                  [disabled]="saving"
                  (selectionChange)="onSelectionChange($event)"
                  class="chatbot-selection-list bg-light-bg-secondary dark:bg-dark-surface-variant rounded-xl border border-light-border dark:border-dark-border overflow-hidden">
                  @for (chatbot of chatbots; track trackByChatbotId($index, chatbot)) {
                    <mat-list-option
                      [value]="chatbot.chatbotId"
                      [selected]="isSelected(chatbot.chatbotId)"
                      checkboxPosition="before"
                      class="chatbot-list-item hover:bg-light-bg-tertiary dark:hover:bg-dark-surface transition-colors duration-200">
                      <!-- Desktop Layout -->
                      <div class="hidden md:flex items-center justify-between w-full gap-3 py-2">
                        <span class="text-xs text-light-text-tertiary dark:text-dark-text-tertiary font-mono">{{ chatbot.code }}</span>
                        <span class="flex-1 text-sm text-light-text dark:text-dark-text">{{ chatbot.name }}</span>
                        <span
                          class="px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap"
                    [ngClass]="{
                      'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400': chatbot.isActive,
                      'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400': !chatbot.isActive
                    }">
                          {{ chatbot.isActive ? 'Activo' : 'Inactivo' }}
                        </span>
                      </div>
                      <!-- Mobile Layout -->
                      <div class="md:hidden flex flex-col gap-2 py-2 w-full">
                        <div class="flex items-center justify-between">
                          <span class="text-xs text-light-text-tertiary dark:text-dark-text-tertiary font-mono font-semibold">
                            {{ chatbot.code }}
                          </span>
                          <span
                            class="px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap"
                      [ngClass]="{
                        'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400': chatbot.isActive,
                        'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400': !chatbot.isActive
                      }">
                            {{ chatbot.isActive ? 'Activo' : 'Inactivo' }}
                          </span>
                        </div>
                        <span class="text-sm text-light-text dark:text-dark-text">{{ chatbot.name }}</span>
                      </div>
                    </mat-list-option>
                  }
                </mat-selection-list>
              }
              @if (!loading && chatbots.length === 0) {
                <div
                  class="text-center py-12 bg-light-bg-secondary dark:bg-dark-surface-variant rounded-xl">
                  <div class="w-16 h-16 bg-light-bg-tertiary dark:bg-dark-surface rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <lucide-angular
                      [img]="Bot"
                      class="text-4xl text-light-text-tertiary dark:text-dark-text-tertiary"
                    ></lucide-angular>
                  </div>
                  <p class="normal">No hay chatbots disponibles</p>
                </div>
              }
            </div>
            @if (isEditMode && data.user) {
              <div class="border-t border-light-border dark:border-dark-border my-6"></div>
            }
            <!-- Metadata solo en modo edición -->
            @if (isEditMode && data.user) {
              <div class="metadata-section p-4 bg-light-bg-secondary dark:bg-dark-surface-variant rounded-xl space-y-3">
                <!-- Desktop Layout -->
                <div class="hidden md:block space-y-3">
                  <div class="flex justify-between items-center">
                    <span class="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">Creado por:</span>
                    <span class="text-sm text-light-text dark:text-dark-text">{{ data.user.createdBy || '-' }}</span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">Fecha de creación:</span>
                    <span class="text-sm text-light-text dark:text-dark-text">{{ formatDate(data.user.createdAt) }}</span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">Última actualización:</span>
                    <span class="text-sm text-light-text dark:text-dark-text">{{ formatDate(data.user.updatedAt) }}</span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">Actualizado por:</span>
                    <span class="text-sm text-light-text dark:text-dark-text">{{ data.user.updatedBy || '-' }}</span>
                  </div>
                </div>
                <!-- Mobile Layout -->
                <div class="md:hidden space-y-4">
                  <div class="flex flex-col gap-1">
                    <span class="text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary uppercase">Creado por</span>
                    <span class="text-sm text-light-text dark:text-dark-text">{{ data.user.createdBy || '-' }}</span>
                  </div>
                  <div class="flex flex-col gap-1">
                    <span class="text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary uppercase">Fecha de creación</span>
                    <span class="text-sm text-light-text dark:text-dark-text">{{ formatDate(data.user.createdAt) }}</span>
                  </div>
                  <div class="flex flex-col gap-1">
                    <span class="text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary uppercase">Última actualización</span>
                    <span class="text-sm text-light-text dark:text-dark-text">{{ formatDate(data.user.updatedAt) }}</span>
                  </div>
                  <div class="flex flex-col gap-1">
                    <span class="text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary uppercase">Actualizado por</span>
                    <span class="text-sm text-light-text dark:text-dark-text">{{ data.user.updatedBy || '-' }}</span>
                  </div>
                </div>
              </div>
            }
          </form>
        }
      </mat-dialog-content>
    
      <!-- Actions - Always visible at bottom -->
      <mat-dialog-actions align="end" class="flex-shrink-0 bg-light-surface dark:bg-dark-surface border-t border-light-border dark:border-dark-border gap-3">
        <button
          mat-button
          (click)="onCancel()"
          [disabled]="saving"
          class="!text-light-text-secondary dark:!text-dark-text-secondary hover:!bg-light-bg-tertiary dark:hover:!bg-dark-surface-variant !rounded-xl !px-6 !py-5 transition-all duration-200">
          Cancelar
        </button>
        <button
          mat-raised-button
          color="primary"
          (click)="onSave()"
          [disabled]="!userForm?.valid || saving"
          class="modal-action-btn !bg-light-primary dark:!bg-dark-primary !text-white hover:!bg-light-primary-variant dark:hover:!bg-dark-primary-variant !rounded-xl !px-6 !py-5 !shadow-soft hover:!shadow-medium transition-all duration-200">
          <div class="flex items-center justify-center gap-2">
            @if (!saving) {
              <span>{{ isEditMode ? 'Guardar cambios' : 'Crear Usuario' }}</span>
            }
            @if (saving) {
              <span>{{ isEditMode ? 'Guardando...' : 'Creando...' }}</span>
            }
            @if (saving) {
              <mat-spinner diameter="20" class="white-spinner"></mat-spinner>
            }
          </div>
        </button>
      </mat-dialog-actions>
    </div>
    `,
  styles: [`
    .user-edit-modal {
      min-width: 600px;
      max-width: 800px;
      padding: 8px; 
      display: flex;
      flex-direction: column;
    }

    mat-dialog-content {
      overflow-y: auto;
      padding: 0 24px;
    }

    mat-dialog-actions {
      padding: 8px 24px;
      padding-bottom: 0px !important;
    }

    .chatbot-selection-list {
      max-height: 400px;
      overflow-y: auto;
    }

    .chatbot-list-item {
      min-height: 56px;
      border-bottom: 1px solid;
      
      &:last-child {
        border-bottom: none;
      }
    }

    .mat-mdc-progress-spinner { --mdc-circular-progress-active-indicator-color: white; }


    ::ng-deep .chatbot-list-item .mdc-list-item__content {
      width: 100%;
      padding: 0;
    }

    ::ng-deep .white-spinner .mat-mdc-progress-spinner-circle {
      stroke: #fff !important;
    }

    // Material form field customization
    ::ng-deep {
      .mat-mdc-form-field {
        .mat-mdc-text-field-wrapper {
          border-radius: 12px;
        }

        // Remover padding del prefix que ya no existe
        .mat-mdc-form-field-icon-prefix {
          padding: 0;
          display: none;
        }

        .mat-mdc-form-field-text-prefix {
          display: none;
        }
      }
      
      .mat-mdc-dialog-surface {
        border-radius: 24px !important;
        padding: 24px;
        background: #ffffff !important; /* mismo fondo claro que usa el contenedor interno */
      }

      /* Forzar fondo oscuro cuando el sitio está en modo dark (tailwind usa la clase dark) */
      .dark .mat-mdc-dialog-surface {
        background: #1f1f1f !important;
      }      
       
      .mat-mdc-dialog-content {
        padding: 0;
        margin: 16px 0;
      }
      
      .mat-mdc-dialog-actions {
        padding: 0;
        margin: 0;
        min-height: auto;
      }
    }

    // Mobile responsive styles
    @media (max-width: 768px) {
      .user-edit-modal {
        min-width: unset;
        width: 100%;
        max-width: 100vw;
        padding: 4px;
      }

      mat-dialog-content {
        max-height: 60vh;
      }

      .chatbot-selection-list {
        max-height: 300px;
      }

      .chatbot-list-item {
        min-height: 70px;
      }

      ::ng-deep {
        .mat-mdc-dialog-surface {
          border-radius: 16px !important;
          padding: 12px; /* mobile: mantener padding reducido */
          background: #ffffff !important;
          max-width: calc(100vw - 32px);
        }

        .dark .mat-mdc-dialog-surface {
          background: #1f1f1f !important;
        }

        .mat-mdc-dialog-container {
          padding: 16px;
        }
      }
    }
  `]
})
export class UserEditModalComponent implements OnInit {
  userForm: FormGroup | null = null;
  isEditMode: boolean = false;
  saving: boolean = false;

  /** NUEVO: propiedades estables para el template */
  loading: boolean = false;
  chatbots: Chatbot[] = [];
  selectedChatbotIds: Set<number> = new Set();
  existingEmails: Set<string> = new Set();

  readonly Bot = Bot;

  constructor(
    public dialogRef: MatDialogRef<UserEditModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UserEditModalData,
    private fb: FormBuilder,
    private userService: UserManagementService,
    private chatbotService: ChatbotManagementService,
    private snackBar: MatSnackBar
  ) {
    this.isEditMode = !!data.user;
    
    // Cargar emails existentes para validación
    if (!this.isEditMode) {
      this.loadExistingEmails();
    }
  }

  ngOnInit(): void {
    this.initForm();
    this.loadChatbotsFromData();
  }

  /** Cargar emails existentes para validación en modo creación */
  private loadExistingEmails(): void {
    this.userService.getUsers().subscribe({
      next: (users) => {
        this.existingEmails = new Set(users.map(u => u.email.toLowerCase()));
      },
      error: (error) => {
      }
    });
  }

  /** Usar chatbots precargados del componente padre */
  private loadChatbotsFromData(): void {
    if (this.data.allChatbots && this.data.allChatbots.length > 0) {
      this.chatbots = this.data.allChatbots;
      if (this.isEditMode) {
        this.initializeSelectedChatbots();
      }
    } else {
      this.loadAllChatbots();
    }
  }

  /** CARGA chatbots en una SOLA referencia (evita recrear arrays en el template) */
  private loadAllChatbots(): void {
    this.loading = true;

    this.chatbotService.getAllChatbots().subscribe({
      next: (chatbots) => {
        this.chatbots = chatbots;
        this.initializeSelectedChatbots();
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
      }
    });
  }

  /** Inicializa selección según los chatbots del usuario */
  private initializeSelectedChatbots(): void {
    if (!this.data.user) return;

    this.selectedChatbotIds = new Set(this.data.user.chatbots.map(c => c.chatbotId));
  }

  /** Verificar si un chatbot está seleccionado */
  isSelected(chatbotId: number): boolean {
    return this.selectedChatbotIds.has(chatbotId);
  }

  /** Manejar cambios en la selección */
  onSelectionChange(event: any): void {
    // event.source es el mat-selection-list
    // selectedOptions.selected es un array de MatListOption seleccionadas
    const selectedOptions = event.source.selectedOptions.selected;
    
    // Reconstruir el Set con los IDs seleccionados
    this.selectedChatbotIds = new Set(
      selectedOptions.map((option: any) => option.value)
    );
    
  }

  /** trackBy para no recrear filas */
  trackByChatbotId = (_: number, c: Chatbot) => c.chatbotId;

  /** Validador personalizado para email duplicado */
  private emailExistsValidator = (control: any) => {
    if (!control.value) return null;
    
    const email = control.value.toLowerCase();
    if (this.existingEmails.has(email)) {
      return { emailExists: true };
    }
    return null;
  };

  /** Form */
  private initForm(): void {
    if (this.isEditMode && this.data.user) {
      // Modo edición: inicializar con datos del usuario
      this.userForm = this.fb.group({
        email: [this.data.user.email, [Validators.required, Validators.email]],
        isActive: [this.data.user.isActive]
      });
    } else {
      // Modo creación: formulario vacío con validación de email duplicado
      this.userForm = this.fb.group({
        email: ['', [Validators.required, Validators.email, this.emailExistsValidator]],
        isActive: [{ value: true, disabled: true }] // Activo y deshabilitado en modo creación
      });
    }
  }

  // Revisar si el form es válido antes de guardar
  private isFormValid(): boolean {
    return this.userForm !== null && this.userForm.valid;
  }

  // revisar si está en modo edición y hay usuario presente
  private isEditModeAndUserPresent(): boolean {
    return this.isEditMode !== null && this.data.user !== null;
  }

  private enableFormField(fieldName: string): void {
    this.userForm?.get(fieldName)?.enable({ onlySelf: true });
  }

  /** Guardar */
  onSave(): void {
    if (!this.isFormValid()) {
      return;
    }

    this.saving = true;
    // Deshabilitar el campo de email desde el código
    this.userForm?.get('email')?.disable({ onlySelf: true });
    const formValue = this.userForm!.getRawValue();

    const userData: Partial<User> = {
      email: formValue.email,
      isActive: formValue.isActive
    };

    const chatbotIds = Array.from(this.selectedChatbotIds);

    if (this.isEditModeAndUserPresent()) {
      // Modo edición: actualizar usuario existente
      this.userService.updateUser(this.data.user!.userId, userData, chatbotIds)
        .subscribe({
          next: (result) => {
            this.saving = false;
            this.showSuccess('Usuario actualizado correctamente');
            this.dialogRef.close(result);
          },
          error: (error) => {
            this.saving = false;
            if (!this.loading) {
              this.enableFormField('email');
            }
            this.showError(this.getErrorMessage(error));
          }
        });
    } else {
      // Modo creación: crear nuevo usuario
      this.userService.createUser(userData, chatbotIds)
        .subscribe({
          next: (result) => {
            this.saving = false;
            this.showSuccess('Usuario creado correctamente');
            this.dialogRef.close(result);
          },
          error: (error) => {
            this.saving = false;
            if (!this.loading) {
              this.enableFormField('email');
            }
            // Manejar error de email duplicado
            this.handleEmailDuplicateError(error);
          }
        });
    }
  }

  

  /** Mensajes */
  private getErrorMessage(error: any): string {
    if (error.error?.error) {
      return error.error.error;
    }
    if (error.message) {
      return error.message;
    }
    return 'Error al guardar los cambios. Por favor, intente nuevamente.';
  }

  /** Maneja el error de email duplicado y muestra el mensaje adecuado */
  private handleEmailDuplicateError(error: any): void {
    const errorMsg = this.getErrorMessage(error);
    if (errorMsg.toLowerCase().includes('exist') || errorMsg.toLowerCase().includes('duplica')) {
      this.userForm?.get('email')?.setErrors({ emailExists: true });
      this.showError('El email ingresado ya está registrado');
    } else {
      this.showError(errorMsg);
    }
  }

  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Cerrar', {
      duration: 3000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['success-snackbar']
    });
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Cerrar', {
      duration: 5000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['error-snackbar']
    });
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  /** Util */
  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}

